import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PackageOpen,
  Package,
  AlertCircle,
  RefreshCw,
  Loader,
  Trash2
} from 'lucide-react';
import { pOrdersService } from '../../../../services/pOrdersService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrdersDto, POrderLineDto } from '../../../../types/porders';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow } from '../../../shared/table';

// Table row type for working orders
interface WorkingOrderTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  statusText: string;
  orderDate: string | null;
  deliveryDate: string | null;
  supplierNo: string;
  supplierName: string | null;
  yourRef: string | null;
  ourRef: string | null;
  warehouse: string | null;
  projectNo: string | null;
  depNo: string | null;
  // Extra fields (hidden by default)
  supplierOrderNo: string | null;
  carrierName: string | null;
  contactName: string | null;
  eMailAddress: string | null;
  delCustomerName: string | null;
  delAddress1: string | null;
  delZipCode: string | null;
  delCity: string | null;
  deliveryMethod: string | null;
  numOfLines: number | null;
  created: string | null;
  lastUpdate: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  // Internal
  _original: POrdersDto;
  _lines?: POrderLineDto[];
  _linesLoaded?: boolean;
}

// Create column factory
const columnFactory = createColumnFactory<WorkingOrderTableRow>();

interface WorkingOrdersProps {
  onBack?: () => void;
}

const WorkingOrders: React.FC<WorkingOrdersProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();
  const [orders, setOrders] = useState<POrdersDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderLines, setOrderLines] = useState<Map<number, POrderLineDto[]>>(new Map());
  const [loadingLines, setLoadingLines] = useState<Set<number>>(new Set());
  const [selectedOrderData, setSelectedOrderData] = useState<WorkingOrderTableRow[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ orders: { orderNo: number; orderNoERP: string; numOfLines: number | null }[] } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number; failed: string[] } | null>(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pOrdersService.getOrdersForReceiving(currentWarehouse || undefined);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av bestillinger');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadOrderLines = useCallback(async (orderNo: number) => {
    if (orderLines.has(orderNo)) return;

    try {
      setLoadingLines(prev => new Set(prev).add(orderNo));
      const lines = await pOrderLineService.getOrderLines(orderNo);
      setOrderLines(prev => new Map(prev).set(orderNo, lines));
    } catch (err) {
      console.error(`Error loading lines for order ${orderNo}:`, err);
    } finally {
      setLoadingLines(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  }, [orderLines]);

  // Selection change handler
  const handleSelectionChange = useCallback((_selectedRows: (string | number)[], selectedData: WorkingOrderTableRow[]) => {
    setSelectedOrderData(selectedData);
  }, []);

  // Delete orders handler (supports single and bulk)
  const handleDeleteOrders = useCallback(async () => {
    if (!deleteConfirm) return;
    const { orders: ordersToDelete } = deleteConfirm;
    setDeleting(true);
    setDeleteProgress({ current: 0, total: ordersToDelete.length, failed: [] });

    const failed: string[] = [];
    for (let i = 0; i < ordersToDelete.length; i++) {
      const order = ordersToDelete[i];
      setDeleteProgress({ current: i + 1, total: ordersToDelete.length, failed });
      try {
        await pOrdersService.deleteOrder(order.orderNo);
      } catch (err) {
        failed.push(`${order.orderNoERP}: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
      }
    }

    setDeleteConfirm(null);
    setDeleteProgress(null);
    setSelectedOrderData([]);
    setDeleting(false);

    if (failed.length > 0) {
      setError(`Kunne ikke slette ${failed.length} av ${ordersToDelete.length} bestillinger:\n${failed.join('\n')}`);
    }

    await loadOrders();
  }, [deleteConfirm, loadOrders]);

  // Transform orders to table data
  const tableData: WorkingOrderTableRow[] = useMemo(() => {
    return orders.map(order => ({
      id: order.orderNo,
      orderNo: order.orderNo,
      orderNoERP: order.orderNoERP || order.orderNo.toString(),
      orderStatus: order.orderStatus,
      statusText: order.statusText || '',
      orderDate: order.orderDate ? new Date(order.orderDate).toISOString() : null,
      deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString() : null,
      supplierNo: order.supplierNo,
      supplierName: order.supplierName || null,
      yourRef: order.yourRef || null,
      ourRef: order.ourRef || null,
      warehouse: order.warehouse || null,
      projectNo: order.projectNo || null,
      depNo: order.depNo || null,
      // Extra fields
      supplierOrderNo: order.supplierOrderNo || null,
      carrierName: order.carrierName || null,
      contactName: order.contactName || null,
      eMailAddress: order.eMailAddress || null,
      delCustomerName: order.delCustomerName || null,
      delAddress1: order.delAddress1 || null,
      delZipCode: order.delZipCode || null,
      delCity: order.delCity || null,
      deliveryMethod: order.deliveryMethod || null,
      numOfLines: order.numOfLines ?? null,
      created: order.created ? new Date(order.created).toISOString() : null,
      lastUpdate: order.lastUpdate ? new Date(order.lastUpdate).toISOString() : null,
      createdBy: order.createdBy || null,
      updatedBy: order.updatedBy || null,
      _original: order,
      _lines: orderLines.get(order.orderNo),
      _linesLoaded: orderLines.has(order.orderNo)
    }));
  }, [orders, orderLines]);

  // Row ID function
  const getRowId = useCallback((row: WorkingOrderTableRow) => row.orderNo, []);

  // Status color helper
  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Column definitions
  const columns: ColumnDefinition<WorkingOrderTableRow>[] = useMemo(() => [
    // === SYNLIGE HOVEDFELTER ===
    columnFactory.createNumberColumn('orderNo', 'orderNo', 'Ordrenr', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null && value !== undefined ? value.toString() : '-'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'ERP Ordrenr', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', 'Prosjektnr', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('depNo', 'depNo', 'Avdeling', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('supplierName', 'supplierName', 'Leverandør', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createDateColumn('orderDate', 'orderDate', 'Ordredato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', 'Deres ref', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', 'Vår ref', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('warehouse', 'warehouse', 'Lager', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('statusText', 'statusText', 'Ordrestatus', {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.orderStatus)}`}>
          {value}
        </span>
      )
    }),

    // === SKJULTE EKSTRAFELTER ===
    columnFactory.createTextColumn('supplierNo', 'supplierNo', 'Leverandørnr', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('supplierOrderNo', 'supplierOrderNo', 'Lev. ordrenr', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('contactName', 'contactName', 'Kontaktperson', {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('eMailAddress', 'eMailAddress', 'E-post', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delCustomerName', 'delCustomerName', 'Lev. kundenavn', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress1', 'delAddress1', 'Lev. adresse', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delZipCode', 'delZipCode', 'Lev. postnr', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delCity', 'delCity', 'Lev. poststed', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', 'Leveringsmåte', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createNumberColumn('numOfLines', 'numOfLines', 'Ant. linjer', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createDateColumn('created', 'created', 'Opprettet', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', 'Sist oppdatert', {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', 'Opprettet av', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', 'Oppdatert av', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    })
  ], []);

  // Render order lines detail (expandable row content)
  const renderOrderLines = useCallback((order: WorkingOrderTableRow) => {
    const lines = orderLines.get(order.orderNo);
    const isLoadingLinesForOrder = loadingLines.has(order.orderNo);

    if (isLoadingLinesForOrder) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">Laster linjer...</span>
        </div>
      );
    }

    if (!lines) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          Kunne ikke laste linjer
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>Ordrelinjer ({lines.length})</span>
        </h4>

        {lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-600">Linjenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Varenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Beskrivelse</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Antall</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Levert</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Gjenstående</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Enhet</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Lager</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {lines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900 font-medium">{line.lineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.description1 || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.quantity?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.quantityDelivered?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.remainingQuantity?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.itemUnitCode || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.warehouse || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.statusText || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm">
            Ingen ordrelinjer funnet for denne bestillingen
          </div>
        )}
      </div>
    );
  }, [orderLines, loadingLines]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<WorkingOrderTableRow, null> = useMemo(() => ({
    getDetailData: () => null,
    renderDetail: renderOrderLines,
    expandOnRowClick: false,
    onExpand: (row) => {
      loadOrderLines(row.orderNo);
    }
  }), [renderOrderLines, loadOrderLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: loadOrders,
      variant: 'secondary' as const
    }
  ], [loadOrders]);

  // Derived: deletable orders from selection (status < 20)
  const deletableOrders = useMemo(() =>
    selectedOrderData.filter(o => o.orderStatus < 20),
    [selectedOrderData]
  );
  const nonDeletableCount = selectedOrderData.length - deletableOrders.length;
  const canDeleteSelected = deletableOrders.length > 0;

  // Toolbar left content - order count + delete button
  const toolbarLeft = useMemo(() => {
    const buttonLabel = deletableOrders.length > 1
      ? `Slett ${deletableOrders.length} bestillinger`
      : 'Slett bestilling';

    const buttonTitle = selectedOrderData.length === 0
      ? 'Velg en eller flere bestillinger for å slette'
      : !canDeleteSelected
      ? 'Ingen av de valgte bestillingene kan slettes (krever status Ubehandlet eller Godkjent)'
      : nonDeletableCount > 0
      ? `${deletableOrders.length} av ${selectedOrderData.length} valgte kan slettes (${nonDeletableCount} har for høy status)`
      : deletableOrders.length === 1
      ? `Slett bestilling ${deletableOrders[0].orderNoERP}`
      : `Slett ${deletableOrders.length} valgte bestillinger`;

    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-600 font-medium">
          {tableData.length} bestillinger for varemottak
          {selectedOrderData.length > 0 && (
            <span className="ml-2 text-blue-600">({selectedOrderData.length} valgt)</span>
          )}
        </div>
        <button
          onClick={() => {
            if (deletableOrders.length > 0) {
              setDeleteConfirm({
                orders: deletableOrders.map(o => ({
                  orderNo: o.orderNo,
                  orderNoERP: o.orderNoERP,
                  numOfLines: o.numOfLines
                }))
              });
            }
          }}
          disabled={!canDeleteSelected}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
            canDeleteSelected
              ? 'text-red-700 bg-white border border-red-300 hover:bg-red-50 hover:shadow-md cursor-pointer'
              : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
          }`}
          title={buttonTitle}
        >
          <Trash2 size={14} />
          <span>{buttonLabel}</span>
        </button>
      </div>
    );
  }, [tableData.length, selectedOrderData, deletableOrders, nonDeletableCount, canDeleteSelected]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <PackageOpen size={24} />
            <span>Administrer bestillinger</span>
          </h1>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 min-h-0 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader className="animate-spin" size={20} />
              <span>Laster bestillinger...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
            <h3 className="text-red-800 font-medium flex items-center">
              <AlertCircle size={20} className="mr-2" />
              Feil ved lasting av bestillinger
            </h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadOrders}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>Prøv igjen</span>
            </button>
          </div>
        ) : (
          <WarehouseTable
            data={tableData}
            columns={columns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'working-orders-table',
              allowRowSelection: { mode: 'multi', selectAllMode: 'current-page' }
            }}
            getRowId={getRowId}
            onSelectionChange={handleSelectionChange}
            tableActions={tableActions}
            expandableConfig={expandableConfig}
            toolbarLeft={toolbarLeft}
            loading={false}
            error={undefined}
            aria-label="Administrer bestillinger tabell"
          />
        )}
      </div>
      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">Bekreft sletting</h2>
            </div>
            <div className="p-6">
              {deleteConfirm.orders.length === 1 ? (
                <>
                  <p className="text-gray-700">
                    Er du sikker på at du vil slette bestilling <span className="font-semibold">{deleteConfirm.orders[0].orderNoERP}</span> (ordrenr {deleteConfirm.orders[0].orderNo})?
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    Alle tilhørende bestillingslinjer{deleteConfirm.orders[0].numOfLines != null ? ` (${deleteConfirm.orders[0].numOfLines} stk)` : ''} vil også bli slettet. Denne handlingen kan ikke angres.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-700">
                    Er du sikker på at du vil slette <span className="font-semibold">{deleteConfirm.orders.length} bestillinger</span>?
                  </p>
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left py-1.5 px-3 font-medium text-gray-600">ERP Ordrenr</th>
                          <th className="text-left py-1.5 px-3 font-medium text-gray-600">Ordrenr</th>
                          <th className="text-right py-1.5 px-3 font-medium text-gray-600">Ant. linjer</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {deleteConfirm.orders.map(o => (
                          <tr key={o.orderNo}>
                            <td className="py-1.5 px-3 text-gray-900">{o.orderNoERP}</td>
                            <td className="py-1.5 px-3 text-gray-500">{o.orderNo}</td>
                            <td className="py-1.5 px-3 text-gray-500 text-right">{o.numOfLines ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    Alle tilhørende bestillingslinjer vil også bli slettet. Denne handlingen kan ikke angres.
                  </p>
                </>
              )}
              {deleteProgress && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader className="animate-spin" size={14} />
                    <span>Sletter {deleteProgress.current} av {deleteProgress.total}...</span>
                  </div>
                  {deleteProgress.failed.length > 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      {deleteProgress.failed.length} feilet
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteOrders}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>Sletter...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Slett{deleteConfirm.orders.length > 1 ? ` (${deleteConfirm.orders.length})` : ''}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingOrders;
